import { InjectionToken, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterModule, Routes } from '@angular/router';
import {
  AuthGuard,
  AuthService,
  CLIENT_MANAGEMENT_CONFIG_GUARD_FIELD,
  CLIENT_MANAGEMENT_CONFIG_REDIRECT_URL,
  ClientManagementConfigGuard,
  ClientsFeatureEnabledGuard,
  ConfigAndPermissionGuard,
  FEATURE_FLAG_GUARD_FAILURE_ROUTE,
  FEATURE_FLAG_GUARD_FIELDS,
  FEATURE_FLAG_GUARD_INVERT,
  FeatureFlagsGuard,
  GUARD_DATA_COMPOSITE_REDIRECT_TO,
  GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE,
  GUARD_DATA_FEATURE_FLAG_INVERT,
  GUARD_DATA_FEATURE_FLAG_NAMES,
  GUARD_DATA_PERMISSION_ENABLED_FIELD,
  GUARD_DATA_PERMISSION_FAILURE_ROUTE,
  GUARD_DATA_PERMISSION_INVERT,
  InvoicesFeatureEnabledGuard,
  LaunchDarklyFlag,
  LoginPasswordlessComponent,
  LoginSupportComponent,
  PaymentsFeatureEnabledGuard,
  PermissionGuard,
} from '@anduin/auth';
import {
  CONFIG_GUARD_FIELDS,
  CONFIG_GUARD_INVERT,
  CONFIG_GUARD_REDIRECT_URL,
  ConfigGuard,
  GUARD_DATA_CONFIG_ENABLED_FIELD,
} from '@anduin/config';
import {
  AccessErrorPageCause,
  AccessErrorPageComponent,
  CONTAINER_DATA_ERROR_CONTAINER_CALL_TO_ACTION,
  CONTAINER_DATA_ERROR_CONTAINER_MESSAGE,
  ERROR_CONTAINER_CALL_TO_ACTIONS,
  ERROR_CONTAINER_MESSAGES,
  ErrorContainerComponent,
  QUERY_PARAM_CAUSE,
} from '@anduin/shared';
import { ClientsComponent } from './components/clients/clients.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutComponent } from './components/logout/logout.component';
import {
  ROUTE_TOKEN_ACCESS_ERROR,
  ROUTE_TOKEN_ACCOUNTS_RECEIVABLE,
  ROUTE_TOKEN_BILLING_BASE,
  ROUTE_TOKEN_CLIENTS_AND_CONTACTS,
  ROUTE_TOKEN_CLIENT_MANAGEMENT,
  ROUTE_TOKEN_COLLECTIONS_BASE,
  ROUTE_TOKEN_DOCUMENTS,
  ROUTE_TOKEN_DRAFT_INVOICES,
  ROUTE_TOKEN_ENGAGEMENTS,
  ROUTE_TOKEN_HOME,
  ROUTE_TOKEN_INVOICES_BASE,
  ROUTE_TOKEN_OLD_COLLECTIONS_BASE,
  ROUTE_TOKEN_PASSWORDLESS_LOGIN,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PERMISSION_ERROR,
  ROUTE_TOKEN_PRACTICE_OS,
  ROUTE_TOKEN_PROJECTS_BASE,
  ROUTE_TOKEN_REPORTS,
  ROUTE_TOKEN_SETTINGS,
  ROUTE_TOKEN_SUPPORT_LOGIN,
  ROUTE_TOKEN_TIMESHEET,
} from './routing-constants';
import { ReportingOnlyGuard } from './shared/guards/reporting-only-guard.service';

const ACCESS_ERROR_GUARD: InjectionToken<{
  canActivate: CanActivateFn;
}> = new InjectionToken<{
  canActivate: CanActivateFn;
}>('access-error-guard');

const routes: Routes = [
  // note: index.html has been updated to include client-portal in base url
  // <base href="/client-portal">
  {
    // Redirect to login component if no path is specified
    path: '',
    redirectTo: '/login',
    pathMatch: 'full',
  },

  // Un-auth'ed Routes
  {
    // Client login is handled here via Google Identity Platform
    path: 'login',
    component: LoginComponent,
  },
  {
    // Client login is handled here via Google Identity Platform
    path: 'logout',
    component: LogoutComponent,
  },
  {
    // The home page of the app
    path: ROUTE_TOKEN_HOME,
    loadChildren: () => import('@aiwyn/home').then((module) => module.HomeModule),
    canActivate: [AuthGuard, FeatureFlagsGuard],
    data: {
      [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.ENABLE_MY_WORK_HOME_PAGE,
      [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_CLIENT_MANAGEMENT],
      [GUARD_DATA_FEATURE_FLAG_INVERT]: false,
    },
  },

  // Error Routes
  {
    path: `${ROUTE_TOKEN_ACCESS_ERROR}`,
    component: AccessErrorPageComponent,
    canActivate: [ACCESS_ERROR_GUARD],
  },
  // Passwordless Login Routes
  {
    path: `${ROUTE_TOKEN_PASSWORDLESS_LOGIN}`,
    component: LoginPasswordlessComponent,
  },
  // Support Login Route (for Aiwyn staff portal access)
  {
    path: `${ROUTE_TOKEN_SUPPORT_LOGIN}`,
    component: LoginSupportComponent,
  },
  {
    path: `${ROUTE_TOKEN_PERMISSION_ERROR}`,
    component: ErrorContainerComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      [CONTAINER_DATA_ERROR_CONTAINER_MESSAGE]: ERROR_CONTAINER_MESSAGES.GENERIC_PERMISSION_ERROR,
      [CONTAINER_DATA_ERROR_CONTAINER_CALL_TO_ACTION]: ERROR_CONTAINER_CALL_TO_ACTIONS.GENERIC_PERMISSION_ERROR,
      // Check for these permissions to determine if the user has access to the settings page
      [GUARD_DATA_PERMISSION_ENABLED_FIELD]: [
        'collections_can_edit_collections_settings',
        'engagements_manage_engagement_letter_section_templates',
        'engagements_manage_engagement_letter_templates',
        'engagements_manage_job_templates',
        'engagements_can_edit_engagements_settings',
        'billing_edit_auto_generated_settings',
        'billing_manage_invoice_templates',
        'time_management_view_settings',
      ],
      [GUARD_DATA_PERMISSION_FAILURE_ROUTE]: [ROUTE_TOKEN_SETTINGS],
      [GUARD_DATA_PERMISSION_INVERT]: true,
    },
  },

  // Auth'ed Routes
  {
    path: `${ROUTE_TOKEN_INVOICES_BASE}`,
    loadChildren: () => import('@aiwyn/invoices').then((module) => module.InvoicesModule),
    canActivate: [AuthGuard, ReportingOnlyGuard, InvoicesFeatureEnabledGuard],
  },
  {
    path: ROUTE_TOKEN_PAYMENTS,
    loadChildren: () => import('@aiwyn/payments').then((module) => module.PaymentsModule),
    canActivate: [AuthGuard, ReportingOnlyGuard, PaymentsFeatureEnabledGuard, ConfigGuard],
    data: {
      [CONFIG_GUARD_FIELDS]: ['practiceBillingEnabled'],
      [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_ACCOUNTS_RECEIVABLE],
      [CONFIG_GUARD_INVERT]: true,
    },
  },
  {
    path: ROUTE_TOKEN_ACCOUNTS_RECEIVABLE,
    loadChildren: () => import('@aiwyn/payments').then((module) => module.PaymentsModule),
    canActivate: [AuthGuard, ReportingOnlyGuard, PaymentsFeatureEnabledGuard, ConfigGuard],
    data: {
      [CONFIG_GUARD_FIELDS]: ['practiceBillingEnabled'],
      [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_PAYMENTS],
    },
  },
  {
    path: 'controls',
    redirectTo: `${ROUTE_TOKEN_COLLECTIONS_BASE}`,
  },

  // Feature Routes
  {
    path: 'clients',
    component: ClientsComponent,
    canActivate: [AuthGuard, ReportingOnlyGuard, ConfigGuard, ClientsFeatureEnabledGuard],
    data: {
      [CONFIG_GUARD_FIELDS]: ['clientManagementEnabled'],
      [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_CLIENT_MANAGEMENT],
      [CONFIG_GUARD_INVERT]: true,
    },
  },
  {
    path: `${ROUTE_TOKEN_COLLECTIONS_BASE}`,
    loadChildren: () => import('@anduin/collections').then((module) => module.CollectionsModule),
    canActivate: [AuthGuard, ReportingOnlyGuard],
  },
  {
    // Redirect old sequences URL to the new one
    path: `${ROUTE_TOKEN_OLD_COLLECTIONS_BASE}`,
    redirectTo: `${ROUTE_TOKEN_COLLECTIONS_BASE}`,
  },
  {
    path: `${ROUTE_TOKEN_BILLING_BASE}`,
    loadChildren: () => import('@anduin/billing').then((module) => module.BillingModule),
    canActivate: [AuthGuard],
  },
  {
    path: `${ROUTE_TOKEN_DRAFT_INVOICES}`,
    loadChildren: () => import('./features/billing/draft-invoices/draft-invoices.routes').then((m) => m.DRAFT_INVOICES_ROUTES),
    canActivate: [AuthGuard],
  },
  {
    path: `${ROUTE_TOKEN_PROJECTS_BASE}`,
    loadChildren: () => import('@aiwyn/projects').then((module) => module.ProjectsModule),
    canActivate: [AuthGuard],
  },
  {
    path: `${ROUTE_TOKEN_ENGAGEMENTS}`,
    loadChildren: () => import('@aiwyn/engagements').then((module) => module.EngagementsModule),
    canActivate: [AuthGuard, ConfigGuard, PermissionGuard],
    data: {
      [GUARD_DATA_CONFIG_ENABLED_FIELD]: ['engagementsEnabled'],
      [GUARD_DATA_PERMISSION_ENABLED_FIELD]: ['can_view_engagements'],
    },
  },
  {
    path: `${ROUTE_TOKEN_PRACTICE_OS}`,
    loadChildren: () => import('@aiwyn/common/practice-os').then((module) => module.PracticeOsModule),
    canActivate: [AuthGuard, FeatureFlagsGuard],
    data: {
      [FEATURE_FLAG_GUARD_FIELDS]: [LaunchDarklyFlag.ENABLE_ENTITY_MANAGEMENT],
      [FEATURE_FLAG_GUARD_FAILURE_ROUTE]: [ROUTE_TOKEN_HOME],
    },
  },
  {
    path: `${ROUTE_TOKEN_TIMESHEET}`,
    loadChildren: () => import('@aiwyn/timesheet').then((module) => module.TimesheetModule),
    canActivate: [AuthGuard, ConfigGuard],
    data: {
      [GUARD_DATA_CONFIG_ENABLED_FIELD]: ['timesheetEnabled'],
    },
  },
  {
    path: `${ROUTE_TOKEN_SETTINGS}`,
    loadChildren: () => import('@aiwyn/settings').then((module) => module.SettingsModule),
    canActivate: [AuthGuard, ConfigAndPermissionGuard],
    data: {
      [GUARD_DATA_CONFIG_ENABLED_FIELD]: [
        'clientExperiencePortalEnabled',
        'engagementsEnabled',
        'billingEnabled',
        'collectionsEnabled',
        'practiceBillingEnabled',
        'timesheetEnabled',
      ],
      [GUARD_DATA_COMPOSITE_REDIRECT_TO]: [ROUTE_TOKEN_HOME],
      [GUARD_DATA_PERMISSION_ENABLED_FIELD]: [
        'collections_can_edit_collections_settings',
        'engagements_manage_engagement_letter_section_templates',
        'engagements_manage_engagement_letter_templates',
        'engagements_manage_job_templates',
        'engagements_can_edit_engagements_settings',
        'billing_edit_auto_generated_settings',
        'billing_manage_invoice_templates',
        'client_collaboration_can_manage_templates',
        'time_management_view_settings',
      ],
      [GUARD_DATA_PERMISSION_FAILURE_ROUTE]: [ROUTE_TOKEN_PERMISSION_ERROR],
    },
  },
  {
    path: `${ROUTE_TOKEN_CLIENT_MANAGEMENT}`,
    loadChildren: () => import('@aiwyn/clients-and-contacts').then((module) => module.ClientsAndContactsModule),
    canActivate: [AuthGuard, ConfigGuard, FeatureFlagsGuard],
    data: {
      [CONFIG_GUARD_FIELDS]: ['clientManagementEnabled'],
      [CONFIG_GUARD_REDIRECT_URL]: ['clients'],
      [FEATURE_FLAG_GUARD_FIELDS]: [LaunchDarklyFlag.ENABLE_ENTITY_MANAGEMENT],
      [FEATURE_FLAG_GUARD_FAILURE_ROUTE]: [ROUTE_TOKEN_CLIENTS_AND_CONTACTS],
      [FEATURE_FLAG_GUARD_INVERT]: true,
    },
  },
  {
    path: `${ROUTE_TOKEN_CLIENTS_AND_CONTACTS}`,
    loadChildren: () => import('@aiwyn/clients-and-contacts').then((module) => module.ClientsAndContactsModule),
    canActivate: [AuthGuard, ConfigGuard, ClientManagementConfigGuard],
    data: {
      [CONFIG_GUARD_FIELDS]: ['clientManagementEnabled'],
      [CONFIG_GUARD_REDIRECT_URL]: ['clients'],
      [CLIENT_MANAGEMENT_CONFIG_GUARD_FIELD]: 'contactManagementEnabled$',
      [CLIENT_MANAGEMENT_CONFIG_REDIRECT_URL]: [ROUTE_TOKEN_CLIENT_MANAGEMENT],
    },
  },
  {
    path: `${ROUTE_TOKEN_REPORTS}`,
    loadChildren: () => import('@aiwyn/reporting').then((module) => module.ReportingModule),
    canActivate: [AuthGuard, ConfigGuard],
    data: {
      [GUARD_DATA_CONFIG_ENABLED_FIELD]: ['thoughtSpotEnabled'],
      [GUARD_DATA_COMPOSITE_REDIRECT_TO]: [ROUTE_TOKEN_HOME],
      [GUARD_DATA_PERMISSION_FAILURE_ROUTE]: [ROUTE_TOKEN_PERMISSION_ERROR],
    },
  },
  {
    path: `${ROUTE_TOKEN_DOCUMENTS}`,
    loadChildren: () => import('@aiwyn/documents').then((module) => module.DocumentsModule),
    canActivate: [AuthGuard, FeatureFlagsGuard, PermissionGuard],
    data: {
      [FEATURE_FLAG_GUARD_FIELDS]: [LaunchDarklyFlag.ENABLE_SHARE_FILE_LINKS],
      [FEATURE_FLAG_GUARD_FAILURE_ROUTE]: [ROUTE_TOKEN_HOME],
      [GUARD_DATA_PERMISSION_ENABLED_FIELD]: ['documents_view_documents'],
      [GUARD_DATA_PERMISSION_FAILURE_ROUTE]: [ROUTE_TOKEN_PERMISSION_ERROR],
    },
  },
  {
    path: 'redlining-poc',
    loadChildren: () => import('./features/signature-poc/signature-poc.routes').then((m) => m.SIGNATURE_POC_ROUTES),
    canActivate: [AuthGuard, FeatureFlagsGuard],
    data: {
      [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.ENABLE_REDLINING_POC_WORK,
      [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_HOME],
    },
  },
  {
    path: '**',
    redirectTo: `${ROUTE_TOKEN_COLLECTIONS_BASE}`,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload', enableTracing: false, initialNavigation: 'disabled' })],
  exports: [RouterModule],
  providers: [
    {
      provide: ACCESS_ERROR_GUARD,
      useFactory:
        (authService: AuthService, router: Router) =>
        (route: ActivatedRouteSnapshot): boolean => {
          if (
            (route.queryParams[QUERY_PARAM_CAUSE] === AccessErrorPageCause.NOT_CONFIGURED &&
              // If staff does not exist, or staff is disabled, show the "Not configured" error page
              (authService.getStaffId() == null || authService.isStaffDisabled())) ||
            (route.queryParams[QUERY_PARAM_CAUSE] === AccessErrorPageCause.POPUPS_BLOCKED &&
              !authService.isAuthenticated() &&
              authService.loginAttempts > 0)
          ) {
            return true;
          }

          router.navigateByUrl(ROUTE_TOKEN_HOME);
          return false;
        },
      deps: [AuthService, Router],
    },
  ],
})
export class AppRoutingModule {}
